import { useState, useEffect } from 'react'
import './MultiEditModal.css'
import { Device } from '@/models/device'
import { Configuration, PartnerConfigurationService } from '@/services/PartnerConfigurationService'
import { PartnerSimProviderService } from '@/services/PartnerSimProviderService'
import { SimProvider } from '@/models/simProvider'
import { PartnerFirmwareVersionService } from '@/services/PartnerFirmwareVersionService'
import { PartnerCustomFieldService } from '@/services/PartnerCustomFieldService'
import { CustomField, SubCustomField } from '@/models/customField'

interface MultiEditModalProps {
  devices: Device[]
  onClose: () => void
  onSave: (updates: Partial<Device>) => void
}

interface FieldState {
  value: string
  applied: boolean
  error?: string
}

export default function MultiEditModal({ devices, onClose, onSave }: MultiEditModalProps) {
  const [fieldStates, setFieldStates] = useState<Record<string, FieldState>>({
    simProviderId: { value: '', applied: false },
    firmware: { value: '', applied: false },
    configurationId: { value: '', applied: false }
  })
  const [subCustomFieldStates, setSubCustomFieldStates] = useState<Record<string, FieldState>>({})

  const [pendingUpdates, setPendingUpdates] = useState<Partial<Device>>({})
  const [configurations, setConfigurations] = useState<Configuration[]>([])
  const [simProviders, setSimProviders] = useState<SimProvider[]>([])
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [firmwareVersions, setFirmwareVersions] = useState<string[]>([])
  const [loadingConfigurations, setLoadingConfigurations] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingConfigurations(true)
        const [configurationData, customFieldData, simProvidersData, firmwareVersionsData] = await Promise.all([
          PartnerConfigurationService.getAll(),
          PartnerCustomFieldService.getAll(),
          PartnerSimProviderService.getAll(),
          PartnerFirmwareVersionService.getAll()
        ])

        setConfigurations(configurationData.configurations)
        setCustomFields(customFieldData.customFields)
        setSimProviders(simProvidersData.simProviders)
        setFirmwareVersions(firmwareVersionsData.versions)

      } catch (error) {
        console.error(error)
      } finally {
        setLoadingConfigurations(false)
      }
    }

    fetchData()
  }, [])
  
  const validateDuplicates = (field: keyof Device, value: string): string | undefined => {
    if (!value || value === 'blank') return undefined

    // Check for duplicates in selected devices for unique fields
    const uniqueFields = ['deviceSerialNumber', 'imeiNumber', 'phoneNumber', 'simSerialNumber']
    if (uniqueFields.includes(field)) {
      const duplicateCount = devices.filter(device =>
        device[field] === value && device[field] !== ''
      ).length

      if (duplicateCount > 0) {
        return `Duplicate ${field} found. This field must be unique.`
      }
    }

    return undefined
  }

  const handleFieldChange = (field: string, value: string) => {
    const error = validateDuplicates(field as keyof Device, value)
    setFieldStates(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error,
        applied: false // Reset applied state when value changes
      }
    }))
  }

  const handleSubCustomFieldChange = (fieldId: string, value: string) => {
    setSubCustomFieldStates(prev => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        value,
        applied: false
      }
    }))
  }

  const handleApplyField = (field: string) => {
    const fieldState = fieldStates[field]
    if (fieldState.error) return

    let value: any = fieldState.value === 'blank' ? '' : fieldState.value

    // Convert IDs to numbers if they're those fields
    if ((field === 'customFieldId') && value !== '') {
      value = parseInt(value, 10) || null
    }

    setFieldStates(prev => ({
      ...prev,
      [field]: { ...prev[field], applied: true }
    }))

    setPendingUpdates(prev => ({
      ...prev,
      [field]: value || undefined
    }))
  }

  const handleApplySubCustomField = (fieldId: string) => {
    const fieldState = subCustomFieldStates[fieldId]
    if (fieldState.error) return

    let value: any = fieldState.value === 'blank' ? '' : fieldState.value

    setSubCustomFieldStates(prev => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], applied: true }
    }))

    setPendingUpdates(prev => ({
      ...prev,
      [fieldId]: value
    }))
  }

  const handleApplyAllUpdates = (e: React.FormEvent) => {
    e.preventDefault()

    const allSubFields = customFields?.flatMap(cf => cf.subFields ?? []);

    // Check if any fields have errors
    const hasErrors = Object.values(fieldStates).some(state => state.error)
    if (hasErrors) return

    // Only include applied fields
    const appliedUpdates: Partial<Device> = {}
    Object.entries(fieldStates).forEach(([field, state]) => {
      if (state.applied) {
        let value: any = state.value === 'blank' ? '' : state.value

        if (value !== '') {
          appliedUpdates[field as keyof Device] = value as any
        }
      }
    })

    const subCustomFields: string[] = [];
    Object.entries(subCustomFieldStates).forEach(([field, state]) => {
      if (state.applied) {
        let value: any = state.value === 'blank' ? '' : state.value

        subCustomFields.push(value); 
      }
    })

    if (subCustomFields.length > 0) {
      const subCustomFieldItems: SubCustomField[] = subCustomFields.map(scf => {
      const subFieldMeta = allSubFields.find(field => field?.id === scf)!;
      return {
          id: scf,
          name: subFieldMeta.name,
          description: subFieldMeta.description,
          createdAtUtc: subFieldMeta.createdAtUtc,
          isDefault: subFieldMeta.isDefault,
          parentCustomFieldId: subFieldMeta.parentCustomFieldId
        };
      }) || [];
      appliedUpdates.subCustomFields = subCustomFieldItems
    }

    onSave(appliedUpdates)
  }

  const getAppliedCount = () => {
    return Object.values(fieldStates).filter(state => state.applied).length + Object.values(subCustomFieldStates).filter(state => state.applied).length
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Multi Edit</h2>
          <p>Editing {devices.length} device{devices.length > 1 ? 's' : ''}</p>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleApplyAllUpdates} className="multi-edit-form">
          <div className="applied-summary">
            {getAppliedCount() > 0 && (
              <div className="applied-count">
                {getAppliedCount()} field{getAppliedCount() > 1 ? 's' : ''} applied and ready for update
              </div>
            )}
          </div>

          <div className="form-field-group">
            <label htmlFor="simProviderId">SIM Provider</label>
            <div className="field-with-apply">
              <select
                id="simProviderId"
                value={fieldStates.simProviderId.value}
                onChange={(e) => handleFieldChange('simProviderId', e.target.value)}
                className={fieldStates.simProviderId.error ? 'error' : ''}
              >
                <option value="">Select SIM provider</option>
                {simProviders.map((provider) => (
                  <option key={provider.id} value={provider.id.toString()}>
                    {provider.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={`btn-apply ${fieldStates.simProviderId.applied ? 'applied' : ''}`}
                onClick={() => handleApplyField('simProviderId')}
                disabled={!fieldStates.simProviderId.value || !!fieldStates.simProviderId.error}
              >
                {fieldStates.simProviderId.applied ? '✓ Applied' : 'Apply'}
              </button>
            </div>
            {fieldStates.simProviderId.error && <span className="error-text">{fieldStates.simProviderId.error}</span>}
          </div>

          <div className="form-field-group">
            <label htmlFor="firmware">Firmware</label>
            <div className="field-with-apply">
              <select
                id="firmware"
                value={fieldStates.firmware.value}
                onChange={(e) => handleFieldChange('firmware', e.target.value)}
                className={fieldStates.firmware.error ? 'error' : ''}
              >
                <option value="">Select firmware version</option>
                {firmwareVersions !== null && firmwareVersions.length > 0 && firmwareVersions.map((firmwareVersion) => (
                    <option key={firmwareVersion} value={firmwareVersion}>
                      {firmwareVersion}
                    </option>
                ))}
              </select>
              <button
                type="button"
                className={`btn-apply ${fieldStates.firmware.applied ? 'applied' : ''}`}
                onClick={() => handleApplyField('firmware')}
                disabled={!fieldStates.firmware.value || !!fieldStates.firmware.error}
              >
                {fieldStates.firmware.applied ? '✓ Applied' : 'Apply'}
              </button>
            </div>
            {fieldStates.firmware.error && <span className="error-text">{fieldStates.firmware.error}</span>}
          </div>

          <div className="form-field-group">
            <label htmlFor="configurationId">Configuration</label>
            <div className="field-with-apply">
              <select
                id="configurationId"
                value={fieldStates.configurationId.value}
                onChange={(e) => handleFieldChange('configurationId', e.target.value)}
                className={fieldStates.configurationId.error ? 'error' : ''}
                disabled={loadingConfigurations}
              >
                <option value="">
                  {loadingConfigurations ? 'Loading configurations...' : 'Select configuration'}
                </option>
                {configurations.map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={`btn-apply ${fieldStates.configurationId.applied ? 'applied' : ''}`}
                onClick={() => handleApplyField('configurationId')}
                disabled={!fieldStates.configurationId.value || !!fieldStates.configurationId.error}
              >
                {fieldStates.configurationId.applied ? '✓ Applied' : 'Apply'}
              </button>
            </div>
            {fieldStates.configurationId.error && <span className="error-text">{fieldStates.configurationId.error}</span>}
          </div>

          {customFields?.map((field) => (
            <div className="form-field-group">
              <label htmlFor={field.name}>{field.name}</label>
              <div className="field-with-apply">
                <select
                  id={field.name}
                  value={subCustomFieldStates[field.id]?.value || ''}
                  onChange={(e) => handleSubCustomFieldChange(field.id, e.target.value)}
                  className={subCustomFieldStates[field.id]?.error ? 'error' : ''}
                >
                  <option value="">Select custom field</option>
                  {field.subFields?.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={`btn-apply ${subCustomFieldStates[field.id]?.applied ? 'applied' : ''}`}
                  onClick={() => handleApplySubCustomField(field.id)}
                  disabled={!subCustomFieldStates[field.id]?.value || !!subCustomFieldStates[field.id]?.error}
                >
                  {subCustomFieldStates[field.id]?.applied ? 'Applied' : 'Apply'}
                </button>
              </div>
              {subCustomFieldStates[field.id]?.error && (
                <span className="error-text">{subCustomFieldStates[field.id].error}</span>
              )}
          </div>
          ))}

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-apply-all"
              disabled={getAppliedCount() === 0}
            >
              Apply All Updates ({getAppliedCount()} field{getAppliedCount() !== 1 ? 's' : ''})
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}